import { Controller } from "@hotwired/stimulus"

/**
 * Sticky-positioning glue for the inspection pager step (CONEK-7591).
 *
 * The conek_ui `ui--sticky-header` controller does this work on admin pages,
 * but only when the page sits inside the admin layout's mainContent scroller
 * (it resolves the scroller with `closest()`). The pager renders in the
 * hotwire layout, where that scroller does not exist — the gem bails out
 * ("offset disabled"), so this page owns two measurements the gem can't make:
 *
 * - `--inspections-pager-header-offset` on the reference pane, so its
 *   `lg:top-[var(--inspections-pager-header-offset,…)]` sticks BELOW the
 *   pinned header band (sticky CSS cannot reference a previous sibling's
 *   height).
 * - the sticky zone's `top`: the gem's zone classes pin at `top-0`, which on
 *   the hotwire layout is where the page navbar already sits (z-drawer >
 *   zone z-index), so without this the zone's first ~55px paint hidden
 *   underneath the navbar.
 *
 * It also mirrors the gem's `scroll-padding-top` so keyboard/anchor scrolling
 * lands below the header band instead of under it.
 */
export default class extends Controller {
  static GAP = 8 // breathing room, matches ui--sticky-header

  connect() {
    this.wrapper = this.element.closest(
      '[data-controller~="ui--sticky-header"]',
    )
    this.zone = this.wrapper?.querySelector(
      "[data-ui--sticky-header-target='zone']",
    )
    // The hotwire layout's navbar (gem Ui::NavbarComponent, sticky top-0).
    this.navbar = document.querySelector(".group\\/navbar")
    if (!this.zone) return // no sticky header on this page: keep the CSS fallback

    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => this.sync())
      this.resizeObserver.observe(this.zone)
      if (this.navbar) this.resizeObserver.observe(this.navbar)
    }
    this.sync()
  }

  disconnect() {
    this.resizeObserver?.disconnect()
    if (this.zone) {
      this.zone.style.removeProperty("top")
      // scroll-padding-top is a document-global property, not this pane's
      // private state — only clear it if THIS instance wrote it (a
      // bailed-out connect never does).
      document.documentElement.style.removeProperty("scroll-padding-top")
    }
    this.element.style.removeProperty("--inspections-pager-header-offset")
  }

  sync() {
    // offsetHeight (border-box) matches what ui--sticky-header measures.
    const navbarHeight = this.stickyNavbarHeight()
    // Only override the gem's top-0 when a navbar actually occludes; setting
    // "0px" explicitly would be identical anyway but adds noise to the DOM.
    this.zone.style.top = navbarHeight > 0 ? `${navbarHeight}px` : ""
    const offset = navbarHeight + this.zone.offsetHeight + this.constructor.GAP
    this.element.style.setProperty(
      "--inspections-pager-header-offset",
      `${offset}px`,
    )
    // NOTE: scroll-padding on this scrollport does NOT shift the zone's or
    // the pane's own sticky constraint in the pinned Chromium (the desktop
    // suite's `zoneTop == navbarBottom` canary pins that assumption) — the
    // two writes are independent.
    document.documentElement.style.scrollPaddingTop = `${offset}px`
  }

  // 0 unless the navbar is actually pinning over this page (the admin layout
  // positions its navbar outside the scrollport, so it never occludes there).
  stickyNavbarHeight() {
    if (!this.navbar) return 0
    const style = getComputedStyle(this.navbar)
    return style.position === "sticky" || style.position === "fixed"
      ? this.navbar.offsetHeight
      : 0
  }
}
